/**
 * The visible FAQ section.
 *
 * Reads the same `faqs` array that `resolveMeta` turns into `FAQPage` structured
 * data, so the markup and the content on screen are the same data. Three pages
 * previously shipped the schema with nothing on the page to match it — which is
 * a structured-data violation, not a cosmetic gap, and the kind Google issues
 * manual actions for.
 *
 * ## Why `<dl>` rather than `<details>`
 *
 * An accordion hides the answers, and hidden text is worth less to both a reader
 * skimming for one specific worry and to a crawler assessing the page. These
 * answers are short. A definition list renders them all, is the semantically
 * correct element for term/definition pairs, and needs no JavaScript, no state
 * and no keyboard handling of its own — so there is nothing here to get wrong
 * for a screen-reader user.
 */

import type { ReactNode } from 'react'

export const FaqSection = ({
  faqs,
  heading = 'Frequently asked questions',
  intro,
}: {
  faqs?: { q: string; a: string }[]
  heading?: string
  intro?: ReactNode
}) => {
  if (!faqs || faqs.length === 0) return null

  return (
    <section className="mt-14" aria-labelledby="faq-heading">
      <h2 id="faq-heading" className="text-2xl font-bold">
        {heading}
      </h2>
      {intro && <p className="mt-2 max-w-2xl opacity-80">{intro}</p>}
      <dl className="anim-stagger mt-5 space-y-3">
        {faqs.map((faq) => (
          <div key={faq.q} className="rounded-box border border-base-300 bg-base-100 p-4">
            <dt className="font-semibold">{faq.q}</dt>
            <dd className="mt-1.5 text-sm leading-relaxed opacity-80">{faq.a}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}
